import dayjs from "dayjs";
import { format } from "date-fns";
import { UnreachableCaseError } from "libs/errors";
import { DateTime } from "luxon";

/**
 * Formats a dayjs datetime into a relative time string
 * like "tomorrow at 10am" or "today at 10am".
 */
export function convertDateTimeToRelativeString_DayAtTime(datetime: dayjs.Dayjs) {
  if (!datetime) return;

  const now = dayjs();

  const differenceInDays = datetime.startOf("day").diff(now.startOf("day"), "date") / MILLISECONDS_IN_DAY;

  let time = rtf.format(differenceInDays, "day");
  time += " at ";
  time += datetime.get("minutes") === 0 ? format(datetime.toDate(), "haaa") : format(datetime.toDate(), "h:mmaaa");

  return time;
}

export const MILLISECONDS_IN_HOUR = 1000 * 60 * 60;
export const MILLISECONDS_IN_DAY = MILLISECONDS_IN_HOUR * 24;

const rtf = new Intl.RelativeTimeFormat("en", {
  localeMatcher: "best fit",
  numeric: "auto",
  style: "long",
});

// Note, as of 2025/04/01 there is are several issues with `dayjs#format` which cause errors
// to be thrown when calling `#format()` in some cases. We're using `date-fns` as a replacement
// for now.
export function formatRequestDueDate(props: { dueDate: string | DateTime; priority: string | null | undefined }): {
  short: string;
  long: string;
} {
  const { dueDate, priority } = props;

  const isEOD = dueDate instanceof DateTime ? dueDate.equals(dueDate.endOf("day")) : !dueDate.includes("T");

  let date: DateTime<true> | DateTime<false> =
    dueDate instanceof DateTime ? dueDate
    : isEOD ? DateTime.fromISO(dueDate).endOf("day")
    : DateTime.fromISO(dueDate);

  // If the request is an by EOD request, then we choose to interpret the exact time as not
  // being important. Instead, we render the due date as if we're in the sender's time zone.
  // But if the request is due at a specific time, then we want to be sure to render the due
  // date relative to the recipient's current time zone.
  if (!isEOD) {
    date = date.toLocal();
  }

  const toShort = (text: string) => (priority === "100" ? "ASAP" : text);

  if (!date.isValid) {
    return { short: toShort("invalid due date"), long: "invalid due date" };
  }

  const timeRange = getTimeRange(date);

  // Re-usable token fragments
  const timeFmt = "h:mma"; // → 3:45PM
  const weekdayFmt = "cccc"; // → Tuesday
  const dateFmt = "M/d/yyyy"; // → 5/27/2025
  const monthDayFmt = "LLL dd"; // → May 27

  switch (timeRange) {
    case "today": {
      if (isEOD) {
        return { short: toShort("by EOD"), long: "by end of today" };
      }

      return {
        short: toShort(date.toFormat(`'by' ${timeFmt}`)),
        long: date.toFormat(`'by today' ${timeFmt}`),
      };
    }
    case "tomorrow": {
      if (isEOD) {
        return { short: toShort("by tomorrow EOD"), long: "by end of tomorrow" };
      }

      return {
        short: toShort(date.toFormat(`'by tomorrow' ${timeFmt}`)),
        long: date.toFormat(`'by tomorrow' ${timeFmt}`),
      };
    }
    case "yesterday": {
      if (isEOD) {
        return { short: toShort("by yesterday"), long: "by end of yesterday" };
      }

      return {
        short: toShort(date.toFormat(`'by yesterday' ${timeFmt}`)),
        long: date.toFormat(`'by yesterday' ${timeFmt}`),
      };
    }
    case "next 7 days": {
      if (isEOD) {
        return {
          short: toShort(date.toFormat(`'by' ${weekdayFmt} 'EOD'`)),
          long: date.toFormat(`'by end of day on' ${weekdayFmt}, ${dateFmt}`),
        };
      }

      return {
        short: toShort(date.toFormat(`'by' ${weekdayFmt} ${timeFmt}`)),
        long: date.toFormat(`'by' ${timeFmt} 'on' ${weekdayFmt}, ${dateFmt}`),
      };
    }
    case "last 7 days": {
      if (isEOD) {
        return {
          short: toShort(date.toFormat(`'by last' ${weekdayFmt} 'EOD'`)),
          long: date.toFormat(`'by end of day last' ${weekdayFmt}, ${dateFmt}`),
        };
      }

      return {
        short: toShort(date.toFormat(`'by last' ${weekdayFmt} ${timeFmt}`)),
        long: date.toFormat(`'by last' ${weekdayFmt}, ${dateFmt} 'at' ${timeFmt}`),
      };
    }
    case "this year": {
      if (isEOD) {
        return {
          short: toShort(date.toFormat(`'by' ${monthDayFmt} 'EOD'`)),
          long: date.toFormat(`'by end of day on' ${weekdayFmt}, ${dateFmt}`),
        };
      }

      return {
        short: toShort(date.toFormat(`'by' ${monthDayFmt} ${timeFmt}`)),
        long: date.toFormat(`'by' ${weekdayFmt}, ${dateFmt} 'at' ${timeFmt}`),
      };
    }
    case null: {
      if (isEOD) {
        return {
          short: toShort(date.toFormat(`'by' ${dateFmt} 'EOD'`)),
          long: date.toFormat(`'by' ${dateFmt} 'end of day'`),
        };
      }

      return {
        short: toShort(date.toFormat(`'by' ${dateFmt} ${timeFmt}`)),
        long: date.toFormat(`'by' ${dateFmt} ${timeFmt}`),
      };
    }
    default: {
      throw new UnreachableCaseError(timeRange);
    }
  }
}

function getTimeRange(date: DateTime<true>) {
  const now = DateTime.now();

  if (date.hasSame(now, "day")) {
    return "today";
  }

  if (date.hasSame(now.plus({ days: 1 }), "day")) {
    return "tomorrow";
  }

  if (date.hasSame(now.minus({ days: 1 }), "day")) {
    return "yesterday";
  }

  const daysFromNow = date.diff(now, ["day", "hour"]).days;

  if (daysFromNow > 0 && daysFromNow <= 7) {
    return "next 7 days";
  }

  if (daysFromNow < 0 && daysFromNow >= -7) {
    return "last 7 days";
  }

  if (date.hasSame(now, "year")) {
    return "this year";
  }

  return null;
}
